import {assessmentEdit} from "./reassessmentRecord";
import {
  resultReassessment,
  statisticReassessment,
  conversionReassessment,
} from "./teamReassessment";
import { conversionSeasonPolicy } from "./conversionSeasonPolicy";
import { normalizeGoalTotals } from "./adjustTeamStrengths";
import { futureSummary, editResult } from "./teamAnalysis";

export function leagueTeamIds(data) {
  const active = data.state?.active_team_ids;
  const candidates = active?.length
    ? active
    : Object.keys(data.result_teams || {});
  return [...new Set(candidates.map(Number))].filter(
    (id) => data.result_teams?.[id]
  );
}
export function leagueProposals(
  data,
  ids,
  weights,
  pace,
  statPace,
  recent,
  preserve
) {
  return ids.map((id) => ({
    id,
    result: resultReassessment(data, id, weights, pace, recent).rows,
    statistics: [
      ...statisticReassessment(data, id, statPace, !!preserve[id]),
      ...conversionReassessment(data, id, statPace, !!preserve[id]),
    ],
    policy: conversionSeasonPolicy(data.state, id, !!preserve[id]),
  }));
}

// Balance conversion without reversing any individual proposal. The common
// total must lie in both sides' feasible intervals [saved, proposed].
export function balanceConversionProposals(rows) {
  const out = rows.map((t) => ({
    id: t.id,
    values: Object.fromEntries(
      t.statistics
        .filter((r) => r.group === "conversion")
        .map((r) => [r.field, { before: r.before, value: r.value }])
    ),
  }));
  for (const [a, b] of [
    ["home_for", "away_against"],
    ["away_for", "home_against"],
  ]) {
    const sides = [a, b].map((k) => out.map((t) => t.values[k]));
    if (
      sides.some((side) =>
        side.some(
          (r) => !r || !Number.isFinite(r.before) || !Number.isFinite(r.value)
        )
      )
    )
      continue;
    const sums = sides.map((side) => ({
      raw: side.reduce((s, r) => s + r.value, 0),
      low: side.reduce((s, r) => s + Math.min(r.before, r.value), 0),
      high: side.reduce((s, r) => s + Math.max(r.before, r.value), 0),
    }));
    const low = Math.max(...sums.map((s) => s.low)),
      high = Math.min(...sums.map((s) => s.high));
    if (low > high + 1e-12) throw Error("CONVERSION_BALANCE_REQUIRES_REVIEW");
    const target = Math.max(
      low,
      Math.min(high, (sums[0].raw + sums[1].raw) / 2)
    );
    sides.forEach((side, i) => {
      const gap = target - sums[i].raw;
      const capacity = side.reduce(
        (s, r) =>
          s +
          (gap > 0
            ? Math.max(0, r.before - r.value)
            : Math.max(0, r.value - r.before)),
        0
      );
      if (capacity > 0)
        side.forEach((r) => {
          const room =
            gap > 0
              ? Math.max(0, r.before - r.value)
              : Math.max(0, r.value - r.before);
          r.value += (gap * room) / capacity;
        });
    });
  }
  return Object.fromEntries(
    out.map((t) => [
      t.id,
      Object.fromEntries(
        Object.entries(t.values).map(([k, r]) => [k, r.value])
      ),
    ])
  );
}

export function leaguePayload(data, rows) {
  const conversion = balanceConversionProposals(rows);
  const draft = { ...data.result_teams },
    edits = [];
  for (const team of rows) {
    let result = data.result_teams[team.id];
    for (const r of team.result) result = editResult(result, r.field, r.value);
    const assessment = team.result.find(r => r.assessment)?.assessment;
    draft[team.id] = assessment ? {...result, reassessment_request:assessment} : result;
    const statAssessment = team.statistics.find(r => r.assessment)?.assessment;
    if (statAssessment) edits.push(assessmentEdit(team.id, statAssessment));
    if (team.policy)
      edits.push({
        team_id: team.id,
        group: "conversion_policy",
        field: "preserve",
        value: team.policy.preserve ? 1 : 0,
      });
    for (const r of team.statistics)
      edits.push({
        team_id: team.id,
        group: r.group,
        statistic_id: r.statistic_id,
        field: r.field,
        value:
          r.group === "conversion" ? conversion[team.id][r.field] : r.value,
      });
  }
  // Balance matching event totals, not averages of team home factors.
  for (const [forKey, againstKey] of [
    ["for_home", "against_away"],
    ["for_away", "against_home"],
  ]) {
    const values = rows.map(({ id }) => draft[id]);
    const a = values.reduce((n, r) => n + r[forKey], 0),
      b = values.reduce((n, r) => n + r[againstKey], 0);
    if (Math.abs(a - b) > 1e-12 * Math.max(1, a, b)) {
      const balanced = normalizeGoalTotals(
        values.map((r) => ({
          for_total: r[forKey],
          against_total: r[againstKey],
        }))
      );
      rows.forEach(({ id }, i) => {
        draft[id] = futureSummary({
          ...draft[id],
          [forKey]: balanced[i].for_total,
          [againstKey]: balanced[i].against_total,
        });
      });
    }
  }
  // Last operation, shared by preview and save. Includes every statistic and conversion.
  if (data.state) edits.push({ group: "league", field: "normalize", value: 1 });
  const strengths = rows.map(({ id }) => {
    const row = { ...draft[id] };
    for (const key of ["rank", "custom_name", "total_goals"]) delete row[key];
    return row;
  });
  return { draft, strengths, edits };
}

export function normalizedLeagueRows(rows, draft, state) {
  return rows.map((team) => ({
    ...team,
    result: team.result.map((r) => ({ ...r, value: draft[team.id][r.field] })),
    statistics: team.statistics.map((r) => ({
      ...r,
      rawProposal: r.rawProposal ?? r.value,
      value: (r.group === "conversion"
        ? state.teams[team.id].conversion
        : state.teams[team.id].statistics[r.statistic_id])[r.field],
    })),
  }));
}
export function assertLeagueBalance(draft, state, ids) {
  const pairs = (values, keys) => {
    for (const [a, b] of keys) {
      const x = values.reduce((s, v) => s + v?.[a], 0),
        y = values.reduce((s, v) => s + v?.[b], 0);
      if (
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        Math.abs(x - y) > 1e-10 * Math.max(1, x, y)
      )
        throw Error("LEAGUE_NORMALIZATION_FAILED");
    }
  };
  pairs(
    ids.map((id) => draft[id]),
    [
      ["for_home", "against_away"],
      ["for_away", "against_home"],
    ]
  );
  if (state) {
    const active = state.active_team_ids || Object.keys(state.teams);
    for (const key of [
      ...Object.keys(state.reference.statistics),
      "conversion",
    ])
      pairs(
        active.map((id) =>
          key === "conversion"
            ? state.teams[id].conversion
            : state.teams[id].statistics[key]
        ),
        [
          ["home_for", "away_against"],
          ["away_for", "home_against"],
        ]
      );
  }
}
